import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  View,
  type ListRenderItemInfo,
  Animated,
  TouchableOpacity,
  Platform,
} from 'react-native';
import type { StatementType } from '~/credsystem/domain/invoices';
import MonthItem from '../MonthItem';
import { formatDueDate, getMonthName } from '~/credsystem/utils/date';
import { Button, Text } from '~/components';

import MarisaLogoSVG from '~/credsystem/assets/svgs/marisa-logo.svg';
import ArrowLeftSVG from '~/credsystem/assets/svgs/arrow-left.svg';

import { formatMoney } from '~/utils';
import { InvoiceStatus } from '~/credsystem/dto/invoices';
import colors from '~/assets/themes/colors';
import { DraggableScrollView } from '~/components/DraggableScrollView';
import { SCREEN_WIDTH, isWeb } from '~/utils/device';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useAppNavigation from '~/hooks/useAppNavigation';
import { VirtualizedList } from 'react-native';
import type {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from 'react-native';
import { PayInvoiceModal } from '~/credsystem/components/PayInvoiceModal';
import usecredsystemDataStore from '~/credsystem/stores/credsystemData';
import { getEndCardNumber } from '~/utils/string';
import type { RootStackParamList } from '~/navigation/types';
import { useRoute, type RouteProp } from '@react-navigation/native';

const MOBILE_MONTH_ITEM_WIDTH = (SCREEN_WIDTH - 48) / 3;

const SCREEN_WIDTH_LIMIT = 768;

interface Props {
  height: number;
  opacity: {
    default: Animated.AnimatedInterpolation<number>;
    collapsed: Animated.AnimatedInterpolation<number>;
  };
  onChangeMonth: (selectedMonth: StatementType) => void;
}

const Header: React.FC<Props> = ({ height, opacity, onChangeMonth }) => {
  const { goBack, setParams } = useAppNavigation();
  const { top } = useSafeAreaInsets();

  const {
    params: { currentInvoiceId },
  } = useRoute<RouteProp<RootStackParamList, 'Credsystem_Invoices'>>();

  const getInvoices = usecredsystemDataStore.getState().getInvoices;
  const cardNumber =
    usecredsystemDataStore.getState().credsystemCardInfo?.cardNumber;
  const statements =
    usecredsystemDataStore.getState().credsystemCardInfo?.statements;
  const monthsList =
    usecredsystemDataStore.getState().credsystemCardInfo?.statements;

  const [showPayInvoiceModal, setShowPayInvoiceModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<StatementType | null>(
    null
  );
  const [width, setWidth] = useState<number>(0);

  const virtualizedListRef = useRef<VirtualizedList<StatementType>>(null);
  const draggableListRef = useRef<ScrollView>(null);
  const isFirstRender = useRef<boolean>(true);
  const currentIndex = useRef<number>();

  const cardLastFourDigits = useMemo(
    () => getEndCardNumber(cardNumber),
    [cardNumber]
  );

  const colorsHeader = useMemo((): string | undefined => {
    if (selectedMonth) {
      switch (selectedMonth?.status) {
        case InvoiceStatus.WAITING || InvoiceStatus.CLOSED_TO_PAY:
          return colors.primary[600];
        case InvoiceStatus.PAID || InvoiceStatus.TOTAL_PAID:
          return colors.credsystem.success[500];
        case InvoiceStatus.SUED_DELAY ||
          InvoiceStatus.LATE ||
          InvoiceStatus.REFINANCED ||
          InvoiceStatus.MININUM_PAID:
          return colors.credsystem.error[500];
        default:
          return colors.credsystem.gray[500];
      }
    }

    return colors.primary[600];
  }, [selectedMonth]);

  const invoiceTitle = useMemo(() => {
    if (selectedMonth?.status) {
      switch (selectedMonth?.status) {
        case InvoiceStatus.PAID:
          return 'Liquidado';
        case InvoiceStatus.REFINANCED:
          return 'Refinanciado';
        case InvoiceStatus.SUED_DELAY:
          return 'Processado Por Atraso';
        case InvoiceStatus.TOTAL_PAID:
          return 'Pagamento Total Efetuado';
        case InvoiceStatus.MININUM_PAID:
          return 'Pagamento Mínimo Efetuado';
        case InvoiceStatus.LATE:
          return 'Em Atraso';
        case InvoiceStatus.CLOSED_TO_PAY:
          return 'Fechado a Pagar';
        case InvoiceStatus.WAITING:
          return 'Aguardando Fechamento';
        case InvoiceStatus.FUTURE:
          return 'Extrato Futuro';
        default:
          return 'Fatura Aberta';
      }
    }
    return undefined;
  }, [selectedMonth]);

  const invoiceValue = useMemo(
    () => formatMoney(selectedMonth?.invoiceValue ?? 0, true),
    [selectedMonth?.invoiceValue]
  );
  const list = useMemo(() => {
    const emptyStatement: StatementType = {} as StatementType;

    return monthsList
      ? width > SCREEN_WIDTH_LIMIT
        ? [
            emptyStatement,
            emptyStatement,
            ...monthsList,
            emptyStatement,
            emptyStatement,
          ]
        : [emptyStatement, ...monthsList, emptyStatement]
      : [];
  }, [monthsList, width]);

  const renderHeaderTitle = useCallback(
    () => (
      <View className="flex-row items-center mb-9 justify-between mt-6">
        <TouchableOpacity onPress={goBack} activeOpacity={0.6}>
          <ArrowLeftSVG width={20} height={15} color={colors.light[100]} />
        </TouchableOpacity>

        <Text className="text-light-100 text-base">Suas faturas</Text>

        <ArrowLeftSVG width={20} height={15} color={'transparent'} />
      </View>
    ),
    [goBack]
  );

  const changeMonth = useCallback(
    (month: StatementType | null) => {
      setSelectedMonth(month);
      if (month && month?.statementId !== selectedMonth?.statementId) {
        onChangeMonth(month);
      }
    },
    [onChangeMonth, selectedMonth?.statementId]
  );

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<StatementType>) => {
      if (index === 0 || (monthsList && index === monthsList.length + 1)) {
        return <View style={{ width: MOBILE_MONTH_ITEM_WIDTH }} />;
      }
      return (
        <MonthItem
          width={MOBILE_MONTH_ITEM_WIDTH}
          index={index}
          month={getMonthName(item.dueDate)}
          selected={item.statementId === selectedMonth?.statementId}
          onPress={() => {
            if (item.statementId !== selectedMonth?.statementId) {
              virtualizedListRef.current?.scrollToIndex({ index: index - 1 });
              changeMonth(item);
            }
          }}
        />
      );
    },
    [monthsList, selectedMonth?.statementId, changeMonth]
  );

  const onMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const index = Math.floor(
        (event.nativeEvent.contentOffset.x + (SCREEN_WIDTH - 24) / 2) /
          MOBILE_MONTH_ITEM_WIDTH
      );
      if (currentIndex.current === index) {
        return;
      }
      currentIndex.current = index;
      virtualizedListRef.current?.scrollToIndex({
        index,
        animated: true,
        viewPosition: 0.5,
      });

      if (monthsList) {
        changeMonth(monthsList[index - 1]!);
      }
    },
    [changeMonth, monthsList]
  );

  return (
    <View
      onLayout={async (e) => {
        if (isFirstRender.current) {
          isFirstRender.current = false;
          const w =
            (e.nativeEvent.layout.width - 48) /
            (e.nativeEvent.layout.width > SCREEN_WIDTH_LIMIT ? 5 : 3);
          let currentStatements = statements;
          if (!statements) {
            await getInvoices();
            currentStatements =
              usecredsystemDataStore.getState().credsystemCardInfo?.statements;
          }
          if (currentInvoiceId) {
            const statement = currentStatements?.find(
              (month) => month.statementId === Number(currentInvoiceId)
            );
            if (statement) {
              changeMonth(statement);
            }
            const index = currentStatements?.findIndex(
              (month) => month.statementId === Number(currentInvoiceId)
            );
            setTimeout(() => {
              virtualizedListRef.current?.scrollToIndex({
                index: index ?? 0 + 1,
              });
              draggableListRef.current?.scrollTo({
                x: w * (index ?? 0 - 1),
              });
            }, 500);
          }
        }
        setWidth(e.nativeEvent.layout.width);
      }}
      style={{
        backgroundColor: colorsHeader,
        height,
        width: '100%',
        paddingTop: Platform.OS === 'android' ? top + 16 : top,
      }}
    >
      <Animated.View
        className={`px-6 absolute top-0 w-full justify-end ${
          selectedMonth?.hasBill ? 'mt-[-16px]' : ''
        }`}
        style={{ opacity: opacity.collapsed, height }}
      >
        {renderHeaderTitle()}
        <View className="flex-row items-start justify-between">
          <MarisaLogoSVG />

          <Text className="text-light-100 text-xs font-medium">
            Final {cardLastFourDigits}
          </Text>
        </View>

        <View className="w-full h-[0.5px] bg-gray-100 my-3" />

        {selectedMonth && selectedMonth.hasBill ? (
          <View className="flex-row items-center justify-between mb-10">
            <View className="flex-row items-center">
              <Text className="text-lg text-light-100">
                {getMonthName(selectedMonth.dueDate)}
              </Text>

              <Text className="text-lg text-light-100 ml-3">
                {invoiceValue}
              </Text>
            </View>

            <Button
              variant="secondary"
              className="border-light-100 bg-transparent py-2"
              onPress={() => setShowPayInvoiceModal(true)}
            >
              <Text className="text-light-100 text-xs font-medium">
                PAGAR FATURA
              </Text>
            </Button>
          </View>
        ) : (
          <View className="flex-row items-center justify-between mb-8">
            {!!selectedMonth && (
              <Text className="text-lg text-light-100">
                {getMonthName(selectedMonth.dueDate)}
              </Text>
            )}

            <Text className="text-lg text-light-100">{invoiceValue}</Text>
          </View>
        )}
      </Animated.View>

      <Animated.View className="px-6" style={{ opacity: opacity.default }}>
        {renderHeaderTitle()}

        <View className="flex-row items-start justify-between">
          <MarisaLogoSVG />

          <Text className="text-light-100 text-xs font-medium">
            Final {cardLastFourDigits}
          </Text>
        </View>

        <View className="w-full h-[0.5px] bg-gray-100 mt-4 mb-6" />

        {isWeb ? (
          <View className="items-center">
            <DraggableScrollView
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                justifyContent: 'center',
              }}
              ref={draggableListRef}
              centerContent
              style={{
                width: '100%',
              }}
            >
              {list?.map((item, index) => {
                const w = (width - 48) / (width > SCREEN_WIDTH_LIMIT ? 5 : 3);
                if (!item.hasOwnProperty('statementId')) {
                  return <View style={{ width: w }} key={`${index}-hidden`} />;
                }

                return (
                  <View style={{ width: w }} key={item.statementId}>
                    <MonthItem
                      width={w}
                      index={index}
                      month={getMonthName(item.dueDate)}
                      selected={item.statementId === selectedMonth?.statementId}
                      onPress={() => {
                        if (item.statementId !== selectedMonth?.statementId) {
                          setParams({ currentInvoiceId: item.statementId });
                          changeMonth(item);
                          draggableListRef.current?.scrollTo({
                            x:
                              w *
                              (index - (width > SCREEN_WIDTH_LIMIT ? 2 : 1)),
                          });
                        }
                      }}
                    />
                  </View>
                );
              })}
            </DraggableScrollView>
          </View>
        ) : (
          <VirtualizedList
            onScrollToIndexFailed={(info) => {
              new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
                virtualizedListRef.current?.scrollToIndex({
                  index: info.index,
                  animated: true,
                });
              });
            }}
            ref={virtualizedListRef}
            data={monthsList ? [{}, ...monthsList, {}] : []}
            horizontal
            renderItem={renderItem}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item, index) =>
              item ? String(`${item.statementId}-${index}`) : String(index)
            }
            getItemCount={() => (monthsList ? monthsList?.length + 2 : 2)}
            onMomentumScrollEnd={onMomentumScrollEnd}
            decelerationRate={'fast'}
            getItem={(data, index) => data[index]}
          />
        )}

        <View className="items-center justify-center mt-[14px]">
          <Text className="text-xs text-light-100 mb-1">{invoiceTitle}</Text>

          <Text className="text-[26px] text-light-100">{invoiceValue}</Text>
        </View>

        <View className="flex-row items-center justify-between mt-5">
          <Text className="text-light-100 text-xs">Vencimento</Text>
          <Text className="text-light-100 text-base">
            {formatDueDate(selectedMonth?.dueDate ?? '')}
          </Text>
        </View>

        <View className="w-full h-[0.5px] bg-gray-100 my-2" />

        <View className="flex-row items-center justify-between mb-4">
          <Text className="text-light-100 text-xs">Fechamento</Text>
          <Text className="text-light-100 text-base">
            {formatDueDate(selectedMonth?.closure ?? '')}
          </Text>
        </View>

        {selectedMonth?.hasBill && (
          <Button
            variant="secondary"
            className="border-light-100 bg-transparent mb-6"
            onPress={() => setShowPayInvoiceModal(true)}
          >
            <Text className="text-light-100 font-medium">PAGAR FATURA</Text>
          </Button>
        )}
      </Animated.View>

      <View
        className={`rounded-t-[16px] items-center justify-center w-full py-4 ${
          selectedMonth?.hasBill ? 'mt-2' : 'mt-4'
        }`}
        style={{ backgroundColor: colors.credsystem.light[200] }}
      >
        <View className="bg-gray-300 w-[48px] h-[4px] rounded-[10px]" />
      </View>

      <PayInvoiceModal
        visible={showPayInvoiceModal}
        invoiceData={{
          invoiceValue: selectedMonth?.currentBalance,
          dueDate: selectedMonth?.dueDate,
          invoiceId: selectedMonth?.statementNumber,
          status: selectedMonth?.status,
        }}
        onClose={() => setShowPayInvoiceModal(false)}
      />
    </View>
  );
};
export default Header;
